import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import LandingNav from '../shared/LandingNav';
import StickyCTA from '../shared/StickyCTA';
import SiteFooter from '../shared/SiteFooter';
import ImageViewer from '../shared/ImageViewer';
import FeedbackSlideIn from '../shared/FeedbackSlideIn';
import useFeedbackTrigger from '../../hooks/useFeedbackTrigger';
import analyticsService from '../../services/analyticsService';
import { sectionRenderers, defaultSectionOrder } from './HomeSectionRenderers';
import LandingBackdrop from '../landing/LandingBackdrop';
import defaultSupportedBrokers from '../../data/shared/defaultSupportedBrokers.json';
import '../../pages/Landing.css';
import '../../pages/HomeDesk.css';
import { restoreLandingAccentOverride, applyLandingAccentOverride } from '../../utils/landingAccent';
import useLandingStylingParams from '../../hooks/useLandingStylingParams';
import { isPrerendering } from '../../utils/prerendering';

/**
 * Pieces that fade in on scroll. Must match the `.desk-motion` rules in HomeDesk.css.
 * Observed one by one so a title, block, or image lower in a section still plays
 * when a slower scroll reaches it — not when the section first peeks in.
 */
const DESK_REVEAL_SELECTOR = [
  '.landing-section-eyebrow',
  '.landing-section-title',
  '.landing-comparison-intro',
  '.landing-mechanism-headline',
  '.landing-mechanism-intro',
  '.landing-rule-demo-headline',
  '.landing-rule-demo-subheadline',
  '.landing-workflow-proof-headline',
  '.landing-workflow-proof-intro',
  '.landing-start-path-headline',
  '.landing-start-path-intro',
  '.landing-secondary-cta-process',
  '.landing-secondary-cta-closing-line',
  '.landing-social-proof-banner-eyebrow',
  '.landing-social-proof-banner-title',
  '.landing-mechanism-pillar-reveal',
  '.landing-mechanism-showcase',
  '.landing-rule-demo-how__step',
  '.landing-rule-demo-rules',
  '.landing-rule-demo-image',
  '.landing-rule-demo-modes-row',
  '.landing-workflow-proof-figure',
  '.landing-workflow-proof-copy',
  '.landing-start-path-step',
  '.landing-faq-card',
  '.landing-social-proof-banner-card',
  '.landing-comparison-table-scroll',
  '.landing-secondary-cta .landing-cta-btn',
  '.landing-honesty-note--cta',
].join(', ');

/**
 * JSON-driven marketing home template (`/home` via pages/Home.js).
 * Precision Desk visual world: `.home-desk` (see DESIGN.md + HomeDesk.css).
 * Renders sections from config in sectionOrder — add types in HomeSectionRenderers.js.
 * @param {Object} config - Home page config (from JSON)
 */
const HomeTemplate = ({ config }) => {
  const { user } = useContext(AuthContext);
  const { isDark } = useTheme();
  const [imageViewer, setImageViewer] = useState({ isOpen: false, src: '', alt: '' });
  const { shouldShow: showFeedback, triggerReason, markCtaClicked, markCtaHover, dismissFeedback, isTestMode } = useFeedbackTrigger();
  const landingPageRef = useRef(null);
  const { stylingAllowed, accentFromUrl } = useLandingStylingParams();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const root = landingPageRef.current;
    if (!root || isPrerendering() || typeof IntersectionObserver === 'undefined') return undefined;

    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (motionQuery.matches) return undefined;

    const pieces = Array.from(root.querySelectorAll(DESK_REVEAL_SELECTOR));
    if (!pieces.length) return undefined;

    // Already in the reading band on first paint — show it, don't replay an entrance.
    const alreadySettled = (el) => {
      const rect = el.getBoundingClientRect();
      return rect.top < window.innerHeight * 0.58 && rect.bottom > 40;
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-inview');
          observer.unobserve(entry.target);
        });
      },
      // Start once the piece is clearly on screen, without holding it in the lower third.
      { rootMargin: '0px 0px -12% 0px', threshold: 0.05 }
    );

    pieces.forEach((piece) => {
      if (alreadySettled(piece)) {
        piece.classList.add('is-inview', 'is-inview--immediate');
      } else {
        observer.observe(piece);
      }
    });

    root.classList.add('desk-motion');

    // Backup only for pieces already sitting in view (tall blocks can miss a
    // high threshold). Offscreen pieces stay armed so a slow scroll still animates them.
    const rescueVisible = () => {
      pieces.forEach((piece) => {
        if (piece.classList.contains('is-inview')) return;
        const rect = piece.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.52 && rect.bottom > window.innerHeight * 0.18) {
          piece.classList.add('is-inview');
          observer.unobserve(piece);
        }
      });
    };
    const rescueTimer = window.setInterval(rescueVisible, 900);

    const onMotionChange = () => {
      if (motionQuery.matches) root.classList.remove('desk-motion');
    };
    motionQuery.addEventListener('change', onMotionChange);

    return () => {
      observer.disconnect();
      window.clearInterval(rescueTimer);
      motionQuery.removeEventListener('change', onMotionChange);
    };
  }, []);

  useEffect(() => {
    if (!stylingAllowed) return;
    if (accentFromUrl) {
      applyLandingAccentOverride(landingPageRef.current, accentFromUrl);
    } else {
      restoreLandingAccentOverride(landingPageRef.current);
    }
  }, [stylingAllowed, accentFromUrl]);

  const ctaUrl =
    config?.hero?.ctaLink ||
    config?.defaultCta?.link ||
    '/register';
  const ctaLabel = config?.hero?.ctaText || config?.secondaryCta?.ctaText || 'Enforce Your Rules';

  const trackCta = (section) => {
    analyticsService.trackTradingEvent?.('cta_button_clicked', {
      variant: `landing-${section}`,
      user_id: user?.id || null,
      is_logged_in: !!user,
    });
  };

  const handleCtaClick = (section) => {
    if (!user) {
      markCtaClicked();
      try {
        sessionStorage.setItem('registration_redirect', '/pricing');
      } catch (e) { /* ignore */ }
    }
    trackCta(section);
  };

  const openImageViewer = (src, alt) => {
    setImageViewer({ isOpen: true, src, alt });
  };

  const closeImageViewer = () => {
    setImageViewer({ isOpen: false, src: '', alt: '' });
  };

  const CtaButton = ({ section, large = false }) => (
    <Link
      to={ctaUrl}
      className={`landing-cta-btn ${large ? 'landing-cta-btn-large' : ''}`}
      onClick={() => handleCtaClick(section)}
      onMouseEnter={() => !user && markCtaHover()}
    >
      {ctaLabel}
    </Link>
  );

  if (!config) return null;

  const sectionOrder = config.sectionOrder || defaultSectionOrder;

  const getSectionData = (key) => {
    if (key === 'supportedBrokers' && !config.supportedBrokers) {
      return defaultSupportedBrokers;
    }
    return config[key];
  };

  const helpers = {
    ctaUrl,
    ctaLabel,
    handleCtaClick,
    CtaButton,
    openImageViewer,
    user,
    landingConfigId: config?.id || null,
  };

  return (
    <div ref={landingPageRef} className={`landing-page landing-template home-desk${isDark ? '' : ' landing-theme-light'}`}>
      <LandingBackdrop />
      <div className="landing-content-shell">
        <LandingNav />

        {sectionOrder.map((key) => {
          const data = getSectionData(key);
          const renderer = sectionRenderers[key];
          if (!data || !renderer) return null;
          return <React.Fragment key={key}>{renderer(data, helpers)}</React.Fragment>;
        })}

        <SiteFooter
          containerClass="landing-container"
          showRiskDisclaimer={true}
          showTradeStationDisclosure={true}
        />
      </div>
      <StickyCTA
        ctaText={ctaLabel}
        ctaLink={ctaUrl}
        copy="Your process matters as much as your strategy. 14-day free trial."
        onCtaClick={markCtaClicked}
        onCtaHover={markCtaHover}
      />
      <ImageViewer
        isOpen={imageViewer.isOpen}
        imageSrc={imageViewer.src}
        imageAlt={imageViewer.alt}
        onClose={closeImageViewer}
      />
      {(!user || isTestMode) && (
        <FeedbackSlideIn
          isOpen={showFeedback}
          onClose={dismissFeedback}
          triggerReason={triggerReason}
          primaryQuestion="What do you think of PrecisionTrader?"
        />
      )}
    </div>
  );
};

export default HomeTemplate;
